import { Button } from "../ui/button";
import toast from "react-hot-toast";
import { useTranslation } from "../../i18n";
import { useCampaignSql } from "../../features/super/hooks/useCampaigns";

/**
 * "Copy SQL" for the campaign settings.
 *
 * One endpoint serves both cases: the generated query is a pure function of
 * (trigger type, config), so this works while creating a campaign (no id
 * exists yet) and while editing one. It reflects the config currently in the
 * form, including unsaved edits, which is what an operator checking the
 * audience actually wants to see.
 *
 * Rendering it in the settings form rather than only on the stats panel means
 * the audience query can be sanity-checked while composing the campaign
 * instead of only after it exists.
 */
export default function CopySqlButton({ triggerType, config }) {
  const { t } = useTranslation();
  const sqlMutation = useCampaignSql();

  const handleCopy = async () => {
    if (!triggerType) {
      toast.error(t("campaign.selectTrigger"));
      return;
    }
    try {
      const { sql } = await sqlMutation.mutateAsync({ triggerType, config });
      await navigator.clipboard.writeText(sql);
      toast.success(t("campaign.sqlCopied"));
    } catch (err) {
      // A trigger without a generate_sql implementation answers 400 with a
      // readable reason; surface it instead of a generic failure.
      toast.error(
        typeof err === "string" && err.trim()
          ? err
          : t("campaign.sqlCopyFailed"),
      );
    }
  };

  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={handleCopy}
      disabled={sqlMutation.isPending}
    >
      {sqlMutation.isPending ? t("campaign.copyingSql") : t("campaign.copySql")}
    </Button>
  );
}
